if (!customElements.get('sticky-atc-panel')) {
  class K2StickyPurchase extends HTMLElement {
    connectedCallback() {
      if (this.bound || this.initializationObserver) return;
      if (this.bindPurchase()) return;
      // Theme Editor can connect an existing custom element before its children.
      this.initializationObserver = new MutationObserver(() => this.bindPurchase());
      this.initializationObserver.observe(this.closest('.cc-main-product') || this.parentNode || this, { childList: true, subtree: true });
      this.initializationTimeout = setTimeout(() => this.stopInitialization(), 3000);
    }

    stopInitialization() {
      this.initializationObserver?.disconnect();
      this.initializationObserver = null;
      clearTimeout(this.initializationTimeout);
    }

    bindPurchase() {
      if (this.bound) return true;
      this.section = this.closest('.cc-main-product');
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.querySelector('[data-sticky-purchase]');
      this.variantLabel = this.querySelector('[data-sticky-variant]');
      this.mainButton = this.form?.querySelector('[name="add"]');
      if (!this.section || !this.form || !this.button || !this.mainButton) return false;
      this.stopInitialization();
      this.bound = true;
      this.dataset.k2StickyReady = 'true';
      this.sync = this.syncState.bind(this);
      this.onClick = this.purchase.bind(this);
      this.onScroll = this.queueVisibility.bind(this);
      this.header = document.querySelector('header.header__grid');
      this.onVariant = () => requestAnimationFrame(() => {
        this.syncState();
        this.updateVisibility();
      });
      this.button.addEventListener('click', this.onClick);
      this.section.addEventListener('change', this.onVariant);
      this.section.addEventListener('input', this.onVariant);
      this.section.addEventListener('on:variant:change', this.onVariant);
      this.onError = () => {
        this.syncState();
        const error = this.form.querySelector('.js-form-error');
        if (error && !error.hidden) {
          error.setAttribute('tabindex', '-1');
          error.scrollIntoView({ block: 'center', behavior: 'auto' });
          error.focus({ preventScroll: true });
        }
      };
      this.section.addEventListener('on:cart:error', this.onError);
      this.form.addEventListener('submit', this.onVariant);
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.mainButton, { attributes: true, childList: true, subtree: true });
      this.syncState();
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(this.onScroll);
        this.visibilityObserver.observe(this.mainButton);
      }
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.header?.addEventListener('transitionend', this.onScroll);
      if ('ResizeObserver' in window && this.header) {
        this.headerObserver = new ResizeObserver(this.onScroll);
        this.headerObserver.observe(this.header);
      }
      // Native drawers and semantic cookie/dialog panels can open without a scroll.
      this.dialogObserver = new MutationObserver((records) => {
        if (records.some((record) => record.target !== this && !this.contains(record.target))) this.queueVisibility();
      });
      this.dialogObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'open', 'style'] });
      this.updateVisibility();
      return true;
    }

    disconnectedCallback() {
      this.stopInitialization();
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.header?.removeEventListener('transitionend', this.onScroll);
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
      this.button?.removeEventListener('click', this.onClick);
      this.section?.removeEventListener('change', this.onVariant);
      this.section?.removeEventListener('input', this.onVariant);
      this.section?.removeEventListener('on:variant:change', this.onVariant);
      this.section?.removeEventListener('on:cart:error', this.onError);
      this.form?.removeEventListener('submit', this.onVariant);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.bound = false;
      delete this.dataset.k2StickyReady;
      delete this.dataset.k2StickyHidden;
    }

    syncState() {
      const id = this.form.querySelector('[name="id"]');
      const mainText = this.mainButton.textContent.trim();
      const busy = this.mainButton.getAttribute('aria-disabled') === 'true' || this.mainButton.classList.contains('is-loading');
      const picker = this.section.querySelector('variant-picker');
      const variantMismatch = picker?.variant?.id != null && String(picker.variant.id) !== String(id?.value);
      const unresolved = !id?.value || variantMismatch || Boolean(picker && (picker.preSelection || picker.variant === false || picker.variant === null));
      this.syncVariantLabel(picker, id?.value, unresolved);
      this.button.textContent = unresolved && !busy ? this.dataset.chooseOptions : mainText;
      this.button.disabled = busy || this.mainButton.disabled && !unresolved;
      this.button.setAttribute('aria-busy', String(busy));
      this.button.dataset.action = unresolved ? 'choose' : 'add';
      const mainPrice = this.section.querySelector('.product-info .price');
      const stickyPrice = this.querySelector('.card__info .price');
      if (mainPrice && stickyPrice && mainPrice.innerHTML !== stickyPrice.innerHTML) {
        stickyPrice.innerHTML = mainPrice.innerHTML;
      }
    }

    syncVariantLabel(picker, variantId, unresolved) {
      if (!this.variantLabel) return;
      const variant = picker?.variant;
      let label = '';
      if (!unresolved && variant && String(variant.id) === String(variantId)) {
        const options = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
        const values = options.filter((value) => typeof value === 'string').map((value) => value.trim()).filter(Boolean);
        if (values.length && values.every((value) => value.toLowerCase() !== 'default title')) {
          label = values.join(' / ');
        } else if (!values.length && typeof variant.title === 'string' && variant.title.trim().toLowerCase() !== 'default title') {
          label = variant.title.trim();
        }
      }
      this.variantLabel.textContent = label;
      this.variantLabel.hidden = !label;
      this.variantLabel.dataset.k2DefaultVariant = String(/^(default|default title)$/i.test(label));
    }

    syncFreightLabel() {
      if (!matchMedia('(max-width: 768.98px)').matches) return;
      const info = this.querySelector('.card__info');
      if (!info) return;
      const estimate = this.section?.querySelector('[data-pdp-shipping-estimate]');
      const line = estimate?.querySelector('.delivery-stock-fee__fee-line');
      const text = line?.textContent.trim() || '';
      const known = estimate && estimate.getAttribute('aria-busy') !== 'true' && !estimate.classList.contains('is-hidden') && /^Truck freight to \d{5}(?:-\d{4})?: .+/.test(text);
      if (!this.freightLabel && known) {
        this.freightLabel = document.createElement('p');
        this.freightLabel.className = 'k2-sticky-purchase__freight';
        info.appendChild(this.freightLabel);
      }
      if (this.freightLabel) {
        if (this.freightLabel.textContent !== (known ? text : '')) this.freightLabel.textContent = known ? text : '';
        this.freightLabel.hidden = !known;
      }
    }

    purchase() {
      this.syncState();
      if (this.button.disabled) return;
      if (this.button.dataset.action === 'choose') {
        const picker = this.section.querySelector('variant-picker');
        const target = picker?.querySelector('input:not([type="hidden"]), select, button') || this.mainButton;
        picker?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        target.focus({ preventScroll: true });
        return;
      }
      this.mainButton.click();
      requestAnimationFrame(this.sync);
    }

    queueVisibility() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        if (this.bound) this.updateVisibility();
      });
    }

    visibleHeaderBottom() {
      if (!this.header) return 0;
      const style = getComputedStyle(this.header);
      const rect = this.header.getBoundingClientRect();
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0' || rect.height === 0 || rect.top > 10 || rect.top >= window.innerHeight) return 0;
      return Math.max(0, Math.min(window.innerHeight, rect.bottom));
    }

    hasOpenDialog() {
      return [...document.querySelectorAll('#cart-drawer[aria-hidden="false"], [role="dialog"]:not([aria-hidden="true"]), dialog[open]')].some((dialog) => {
        if (dialog.hidden || this.contains(dialog)) return false;
        const style = getComputedStyle(dialog);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
        const rect = dialog.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      });
    }

    updatePreviewOffset() {
      // Shopify's short preview toolbar is not a store dialog or a full-screen iframe.
      const toolbar = document.getElementById('PBarNextFrameWrapper');
      let offset = 0;
      if (toolbar) {
        const style = getComputedStyle(toolbar);
        const rect = toolbar.getBoundingClientRect();
        if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && rect.width > 0 && rect.height > 0 && rect.height <= 120 && rect.bottom >= window.innerHeight - 2 && rect.top >= window.innerHeight - 120) {
          offset = Math.ceil(window.innerHeight - rect.top);
        }
      }
      const value = offset + 'px';
      if (this.style.getPropertyValue('--k2-sticky-preview-offset') !== value) this.style.setProperty('--k2-sticky-preview-offset', value);
    }

    updateVisibility() {
      if (!this.mainButton) return;
      this.updatePreviewOffset();
      this.syncFreightLabel();
      const mainRect = this.mainButton.getBoundingClientRect();
      const hidden = mainRect.height === 0 || mainRect.bottom > this.visibleHeaderBottom() || this.hasOpenDialog();
      this.dataset.k2StickyHidden = String(hidden);
      this.classList.toggle('sticky-atc-panel--out', hidden);
    }

  }
  customElements.define('sticky-atc-panel', K2StickyPurchase);
}
